import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';

const NAV_LINKS = [
  { id: 'story', key: 'nav.story' },
  { id: 'apartments', key: 'nav.apartments' },
  { id: 'gallery', key: 'nav.gallery' },
  { id: 'location', key: 'nav.location' },
] as const;

export default function SiteHeader() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState<string>('hero');

  // Header scroll behaviour: glass effect + hide on scroll down, show on scroll up
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      if (y > lastY && y > 240) setHidden(true);
      else setHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Section spy
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    ['hero', ...NAV_LINKS.map((l) => l.id), 'inquiry'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setDrawerOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: 'smooth' });
      history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <>
      <header className={`site-header${scrolled ? ' is-scrolled' : ''}${hidden ? ' site-header--hidden' : ''}`}>
        <div className="container site-header__inner">
          <a
            href="#hero"
            className="site-brand"
            onClick={(e) => handleNav(e, 'hero')}
            aria-label={t('brand.name')}
          >
            <span className="site-brand__mark">Villa Spilia</span>
            <span className="site-brand__sub">Stavros · Ithaca</span>
          </a>

          <nav className="site-nav" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => handleNav(e, link.id)}
                aria-current={active === link.id}
              >
                {t(link.key)}
              </a>
            ))}
          </nav>

          <div className="site-header__cta">
            <LanguageSwitcher />
            <a
              href="#inquiry"
              className="btn btn--gold btn--sm"
              onClick={(e) => handleNav(e, 'inquiry')}
            >
              {t('nav.inquiry')}
              <span className="arrow" aria-hidden="true">→</span>
            </a>
          </div>

          <button
            className="nav-toggle"
            aria-label="Menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <span />
          </button>
        </div>
      </header>

      <div className={`mobile-drawer${drawerOpen ? ' is-open' : ''}`} aria-hidden={!drawerOpen}>
        <nav className="mobile-drawer__nav" aria-label="Mobile">
          {NAV_LINKS.map((link) => (
            <a key={link.id} href={`#${link.id}`} onClick={(e) => handleNav(e, link.id)}>
              {t(link.key)}
            </a>
          ))}
          <a href="#inquiry" onClick={(e) => handleNav(e, 'inquiry')}>
            {t('nav.inquiry')}
          </a>
        </nav>
        <div className="mobile-drawer__foot">
          <LanguageSwitcher />
        </div>
      </div>
    </>
  );
}
